'use client';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { startTransition, useEffect } from 'react';

import { RootLayoutClient } from '../../components/RootLayout/RootLayoutClient';
import { StatusPage } from '../../components/StatusPage';
import { captureException } from '../../util/sentryClient';
import { wasReportedOnServer } from '../../util/wasReportedOnServer';

const getErrorKey = (message: string) => {
  if (message === 'API_ERROR_500') return 'temporarilyUnavailable';
  if (message === 'API_ERROR_503') return 'serviceUnavailable';
  if (message === 'API_ERROR_404') return 'companyNotFound';
  return null;
};

const ErrorPage = ({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) => {
  const t = useTranslations('Error');
  const router = useRouter();
  const errorKey = getErrorKey(error.message);
  const isExpectedError = errorKey !== null;
  const isServerError = error.message === 'API_ERROR_500';

  useEffect(() => {
    // Server-Render-Fehler sind ueber onRequestError schon gemeldet, siehe wasReportedOnServer.
    if (wasReportedOnServer(error)) return;
    if (!isExpectedError || isServerError) {
      captureException(error);
    }
  }, [error, isExpectedError, isServerError]);

  const retry = () =>
    startTransition(() => {
      router.refresh();
      reset();
    });

  // Die technische Meldung (z. B. "An unexpected response was received from the server." von
  // Next.js, wenn eine Server Action keine gueltige Antwort bekommt) zeigen wir Bewerbern nicht an,
  // sie landet ueber captureException in Sentry.
  return (
    <RootLayoutClient>
      <StatusPage
        title={t('title')}
        subtitle={errorKey ? t(errorKey) : t('message')}
        actionButton={{ title: t('retry'), onClick: retry }}
      />
    </RootLayoutClient>
  );
};

export default ErrorPage;
